import { Link } from 'react-router-dom';
import styled from 'styled-components';

interface IOpenStatus {
  openStatus: string;
}

interface ITwoColumnSection {
  firstColumnWidth: string;
  secondColumnWidth: string;
}

export const BusinessProfileWrapper = styled.div`
  margin-top: 110px;

  @media ${({ theme }) => `${theme.breakpoints.m}`} {
  }

  @media ${({ theme }) => `${theme.breakpoints.xl}`} {
    margin-top: 110px;
    display: flex;
    flex-direction: column;
  }
`;

export const BackToSearchLink = styled.button`
  grid-row: 1 / span 1;
  grid-column: 1 / span 1;

  width: 200px;
  height: 36px;

  text-align: left;

  color: ${({ theme }) => theme.core.colors.primary.blue.medium};
  background: ${({ theme }) => theme.core.colors.greyscale.white};

  border: none;
`;

export const BusinessDetailsHeader = styled.section`
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: 170px auto 35px 30px 30px;
  justify-items: center;
  align-items: center;

  @media ${({ theme }) => `${theme.breakpoints.s}`} {
    grid-template-columns: 200px auto;
    grid-template-rows: auto auto 35px auto auto;
    justify-items: start;
    column-gap: 45px;
    row-gap: 8px;

    grid-row: 2 / span 1;
    grid-column: 1 / span 1;
  }
`;

export const BusinessLogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  grid-row: 1 / span 1;
  grid-column: 1 / span 1;

  height: 100%;
  width: 150px;

  border-radius: 100%;

  img {
    width: 100%;
  }

  @media ${({ theme }) => `${theme.breakpoints.s}`} {
    grid-column: 1 / span 1;
    grid-row: 1 / span 5;
    align-self: start;
    justify-self: end;
  }
`;

export const BusinessNameContainer = styled.div`
  display: flex;

  gap: ${({ theme }) => theme.sizing[2]}px;
`;

export const BusinessAddress = styled.p`
  margin: 0;

  @media ${({ theme }) => `${theme.breakpoints.s}`} {
    padding: 0;
  }
`;

export const BusinessName = styled.h1`
  margin: 0;

  font-size: 40px;
  font-family: ${({ theme }) => theme.fontFamily.semiBold};
  font-weight: 700;

  line-height: 40px;

  color: ${({ theme }) => theme.core.colors.greyscale.darker};

  @media ${({ theme }) => `${theme.breakpoints.s}`} {
    padding: 0;
  }
`;

export const BusinessCategory = styled.h2`
  display: flex;
  gap: ${({ theme }) => theme.sizing[1]}px;
  align-items: center;

  margin: 0;
  margin-top: 4px;

  a {
    font-size: ${({ theme }) => theme.fontSizes.xl}px;
    font-family: futura-pt, sans-serif;
    font-weight: 400;
    text-transform: capitalize;
    text-decoration: none;

    color: ${({ theme }) => theme.core.colors.greyscale.dark};
  }
`;

export const NoReviewsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing[1]}px;
`;

export const OpenStatus = styled.span<IOpenStatus>`
  grid-row: 6 / span 1;
  grid-column: 1 / span 1;

  align-self: center;

  padding: ${({ theme }) => theme.sizing[0]}px ${({ theme }) => theme.sizing[2]}px;
  border-radius: 8px;

  text-transform: uppercase;
  font-size: ${({ theme }) => theme.fontSizes.sm}px;

  color: ${({ theme }) => theme.core.colors.greyscale.white};
  background: ${({ theme, openStatus }) => {
    if (openStatus === 'openNow') return theme.core.colors.primary.green.medium;
    if (openStatus === 'closedNow') return theme.core.colors.secondary.orange;
    if (openStatus === 'openingSoon') return theme.core.colors.primary.green.medium;
    if (openStatus === 'closingSoon') return theme.core.colors.secondary.orange;
  }};

  @media ${({ theme }) => `${theme.breakpoints.s}`} {
    grid-column: 2 / span 1;
    grid-row: 5 / span 1;
    align-self: center;
  }
`;

export const BusinessDescription = styled.div`
  position: relative;

  font-size: ${({ theme }) => theme.fontSizes.m}px;
  line-height: ${({ theme }) => theme.fontSizes.xl}px;
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;

  color: ${({ theme }) => theme.core.colors.greyscale.black};
`;

export const SectionHeading = styled.h3`
  margin: 16px 0;

  font-size: ${({ theme }) => theme.fontSizes.l}px;
  font-family: 'Open Sans', sans-serif;

  color: ${({ theme }) => theme.core.colors.greyscale.darker};
`;

export const Divider = styled.div`
  position: absolute;
  bottom: -40px;

  width: 100%;
  height: 1px;

  background: ${({ theme }) => theme.core.colors.primary.green.medium};
`;

export const LoadingIndicatorWrapper = styled.div`
  height: calc(100vh - 110px);

  padding-top: 100px;
`;

export const PlaceholderLogo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 150px;
  height: 150px;

  border-radius: 75px;

  color: ${({ theme }) => theme.core.colors.greyscale.white};

  font-size: 80px;
  text-transform: uppercase;

  background: ${({ theme }) => theme.core.colors.primary.green.medium};
`;

export const ScrollToButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing[1]}px;

  background: none;
  border: none;
  padding: 0;
  height: 26px;

  color: ${({ theme }) => theme.core.colors.greyscale.black};

  cursor: pointer;
`;

export const TwoColumnSection = styled.div<ITwoColumnSection>`
  display: flex;
  flex-direction: column;

  width: 100%;

  > section:nth-of-type(1) {
    flex: 0 0 ${({ firstColumnWidth }) => `calc(${firstColumnWidth} - ${80 / 2}px )`};
  }

  > section:nth-of-type(2) {
    flex: 0 0 ${({ secondColumnWidth }) => `calc(${secondColumnWidth} - ${80 / 2}px )`};
  }

  @media ${({ theme }) => `${theme.breakpoints.l}`} {
    flex-direction: row;
    gap: 80px;
  }
`;

export const SingleImage = styled.img`
  width: 100%;

  border-radius: ${({ theme }) => theme.border.container.radius}px;

  @media ${({ theme }) => `${theme.breakpoints.m}`} {
    width: 50%;
  }
`;

export const AgreementLink = styled(Link)`
  margin-top: ${({ theme }) => theme.core.sizing[2]}px;

  font-weight: ${({ theme }) => theme.core.fontWeight.medium};
  font-size: ${({ theme }) => theme.core.fontSizes.l}px;
`;
